import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Player from '@kinescope/react-kinescope-player'
import { useOutsideClick } from '@/hooks'
import {
  useMarkSlideViewedMutation,
  useStoryTracker,
  firstUnviewedSlideIndex,
  type StoryFeedItem,
  type StorySlide,
  type StoryOpenSource,
  type SlideExitReason,
  type LinkTargetType,
} from '@entities/stories'
import { PauseIcon, PlayIcon, CloseIcon, ChevronIcon, SkipIcon, LockIcon } from './icons'
import styles from './StoryViewer.module.css'

interface StoryViewerProps {
  /** Лента историй в том же порядке, что и в трее (непросмотренные — вперёд). */
  stories: StoryFeedItem[]
  /** Индекс истории в `stories`, с которой открылся просмотрщик (тап по кружку). */
  startIndex: number
  /** Вызывается при закрытии просмотрщика (крестик, Esc, докрутка до конца). */
  onClose: () => void
}

/** Сколько показывать картинку до автоперехода. */
const IMAGE_DURATION_MS = 15000
/** Дебаунс отметки слайда просмотренным. */
const MARK_DEBOUNCE_MS = 400
/** Порог удержания, после которого показ ставится на паузу (иначе — тап-листание). */
const HOLD_MS = 200
/** С какого смещения тач-жест считается свайпом вниз, а не тапом. */
const DRAG_START_PX = 8
/** Насколько нужно утянуть карточку вниз, чтобы история закрылась. */
const DRAG_CLOSE_PX = 120
/** Скорость «броска» вниз (px/ms), при которой закрываем без длинного свайпа. */
const DRAG_CLOSE_VELOCITY = 0.5

/** Видео в сторис отдаётся через Kinescope — из URL достаём videoId и watermark. */
const parseKinescope = (url: string): { videoId: string; watermark?: string } => {
  try {
    const u = new URL(url)
    return {
      videoId: u.pathname.split('/').filter(Boolean).pop() || '',
      watermark: u.searchParams.get('watermark') || undefined,
    }
  } catch {
    return { videoId: '' }
  }
}

/**
 * Полноэкранный просмотрщик историй: листает слайды тапами/стрелками, отмечает
 * просмотр и пишет аналитику. См. {@link StoryViewerProps}.
 */
export const StoryViewer = ({ stories, startIndex, onClose }: StoryViewerProps) => {
  const navigate = useNavigate()
  const markMutation = useMarkSlideViewedMutation()
  const tracker = useStoryTracker()

  const [storyIndex, setStoryIndex] = useState(startIndex)
  const [slideIndex, setSlideIndex] = useState(() =>
    firstUnviewedSlideIndex(stories[startIndex]),
  )
  const [paused, setPaused] = useState(false)
  // Смещение карточки при свайпе вниз (0 — жеста нет).
  const [dragY, setDragY] = useState(0)
  // Длительность текущего видео (мс) для полосы прогресса; null — ещё не известна.
  const [videoDurationMs, setVideoDurationMs] = useState<number | null>(null)

  const story = stories[storyIndex]
  const slides: StorySlide[] = story?.slides ?? []
  const slide: StorySlide | undefined = slides[slideIndex]
  const hasAccess = Boolean(story?.has_access)
  const total = stories.length

  // Момент показа текущего слайда — для dwell_ms (учитывает и паузы).
  const slideShownAtRef = useRef(performance.now())
  // Остаток времени картинки (для корректной паузы/резюма).
  const imageRemainingRef = useRef(IMAGE_DURATION_MS)
  const pauseCountRef = useRef(0)
  const playerRef = useRef<Player>(null)
  // Реально ли видео проигрывалось: страховка от «пустого» ended сразу после
  // старта — по нему нельзя листать, иначе история схлопывается.
  const videoProgressedRef = useRef(false)
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  const holdRef = useRef<{ timer: number; dir: 'next' | 'prev' } | null>(null)
  // Свайп вниз: точка старта и признак, что жест уже перерос в перетаскивание
  // (тогда отпускание не должно листать историю).
  const dragRef = useRef<{
    x: number
    y: number
    t: number
    active: boolean
  } | null>(null)
  const draggedRef = useRef(false)
  // Кластер «стрелки + карточка» — клик мимо него (по фону на вебе) закрывает.
  const clusterRef = useRef<HTMLDivElement>(null)

  // Стабильная ссылка на mutate, чтобы колбэки не зависели от состояния мутации.
  const markMutateRef = useRef(markMutation.mutate)
  markMutateRef.current = markMutation.mutate

  // --- Отметка слайда просмотренным (POST /stories/view) с дебаунсом ---
  const pendingRef = useRef<{ slideId: number; timer: number } | null>(null)

  const flushMark = useCallback(() => {
    const pending = pendingRef.current
    if (pending) {
      clearTimeout(pending.timer)
      markMutateRef.current(pending.slideId)
      pendingRef.current = null
    }
  }, [])

  const scheduleMark = useCallback((slideId: number) => {
    if (pendingRef.current) clearTimeout(pendingRef.current.timer)
    const timer = window.setTimeout(() => {
      markMutateRef.current(slideId)
      pendingRef.current = null
    }, MARK_DEBOUNCE_MS)
    pendingRef.current = { slideId, timer }
  }, [])

  // --- Аналитика: метаданные истории + переходы ---
  const storyMeta = useCallback(
    (index: number) => {
      const s = stories[index]
      return {
        storyId: s.story_id,
        storyPosition: index + 1,
        accessType: s.access_type,
        hasAccess: s.has_access,
      }
    },
    [stories],
  )

  /** Открытие истории (аналитика) + переход к ней в UI. */
  const openStoryAt = useCallback(
    (index: number, source: StoryOpenSource) => {
      tracker.openStory(storyMeta(index), source)
      setStoryIndex(index)
      setSlideIndex(firstUnviewedSlideIndex(stories[index]))
    },
    [tracker, storyMeta, stories],
  )

  /** Событие выхода со слайда (если есть доступ и слайд). */
  const leaveSlide = useCallback(
    (reason: SlideExitReason) => {
      if (!hasAccess || !slide) return
      tracker.viewSlide({
        slideId: slide.slide_id,
        slideIndex: slideIndex + 1,
        dwellMs: performance.now() - slideShownAtRef.current,
        pauseCount: pauseCountRef.current,
        exitReason: reason,
      })
    },
    [hasAccess, slide, slideIndex, tracker],
  )

  /** Закрыть текущую историю (аналитика). */
  const endCurrentStory = useCallback(() => {
    const viewedSomething = hasAccess && slides.length > 0
    tracker.closeStory({
      lastSlideIndex: viewedSomething ? slideIndex + 1 : 0,
      isCompleted: viewedSomething ? slideIndex >= slides.length - 1 : false,
    })
  }, [hasAccess, slides.length, slideIndex, tracker])

  /** Финальное закрытие: флаш отметок и батча аналитики. */
  const finish = useCallback(() => {
    flushMark()
    tracker.flush()
    onClose()
  }, [flushMark, tracker, onClose])

  const advance = useCallback(
    (reason: 'tap_forward' | 'auto') => {
      if (hasAccess && slideIndex < slides.length - 1) {
        leaveSlide(reason)
        setSlideIndex(slideIndex + 1)
        return
      }
      // Конец истории (или заглушка без доступа)
      leaveSlide(reason)
      endCurrentStory()
      if (storyIndex < total - 1) {
        openStoryAt(storyIndex + 1, reason === 'auto' ? 'auto_next' : 'swipe_next')
      } else if (reason === 'auto') {
        // Автопрокрутка дошла до конца ленты — закрываем.
        finish()
      } else {
        // Ручной переход «по кругу»: с последней истории — на первую.
        openStoryAt(0, 'swipe_next')
      }
    },
    [
      hasAccess, slideIndex, slides.length, storyIndex, total,
      leaveSlide, endCurrentStory, openStoryAt, finish,
    ],
  )

  const back = useCallback(() => {
    if (hasAccess && slideIndex > 0) {
      leaveSlide('tap_back')
      setSlideIndex(slideIndex - 1)
      return
    }
    leaveSlide('tap_back')
    endCurrentStory()
    // «По кругу»: с первой истории — на последнюю.
    openStoryAt(storyIndex > 0 ? storyIndex - 1 : total - 1, 'swipe_prev')
  }, [hasAccess, slideIndex, storyIndex, total, leaveSlide, endCurrentStory, openStoryAt])

  /** Перескок на соседнюю историю целиком (стрелки на десктопе), циклически. */
  const jumpStory = useCallback(
    (dir: 1 | -1) => {
      leaveSlide(dir === 1 ? 'tap_forward' : 'tap_back')
      endCurrentStory()
      openStoryAt((storyIndex + dir + total) % total, dir === 1 ? 'swipe_next' : 'swipe_prev')
    },
    [leaveSlide, endCurrentStory, openStoryAt, storyIndex, total],
  )

  // Стабильная ссылка на advance для авто-перехода (не в зависимостях эффекта).
  const advanceRef = useRef(advance)
  advanceRef.current = advance

  /**
   * Конец видео. Если ролик фактически не играл (ended прилетел сразу после
   * инициализации плеера), это не настоящий конец — перезапускаем с нуля,
   * а не листаем дальше.
   */
  const handleVideoEnded = useCallback(() => {
    if (!videoProgressedRef.current) {
      const p = playerRef.current
      if (p) {
        void p.seekTo(0).then(() => p.play()).catch(() => {})
        return
      }
    }
    advance('auto')
  }, [advance])

  const close = useCallback(() => {
    leaveSlide('close')
    endCurrentStory()
    finish()
  }, [leaveSlide, endCurrentStory, finish])

  // Веб: клик по фону вне карточки/стрелок закрывает историю.
  useOutsideClick(clusterRef, close)

  /** Покупка подписки из заглушки платной истории: закрыть и уйти на оформление. */
  const buySubscription = useCallback(() => {
    // Уходит сразу отдельным запросом: дальше переход в оплату, батч по
    // закрытию просмотрщика уехать не успеет.
    tracker.clickPaywallCta()
    endCurrentStory()
    finish()
    navigate('/subscribe')
  }, [tracker, endCurrentStory, finish, navigate])

  const handleAction = useCallback(
    (s: StorySlide) => {
      // Слайд получает slide_viewed с exit_reason=link, затем link_clicked.
      leaveSlide('link')
      tracker.clickLink({
        slideId: s.slide_id,
        slideIndex: slideIndex + 1,
        targetType: s.action_type as LinkTargetType,
        targetId: s.action_data ?? '',
      })
      endCurrentStory()
      finish()

      const data = s.action_data ?? ''
      switch (s.action_type) {
        case 'url':
          if (data) window.open(data, '_blank', 'noopener')
          break
        case 'course':
          navigate(`/courses/${data}`)
          break
        case 'recipe':
          navigate(`/card/${data}`)
          break
        case 'page':
          navigate(data.startsWith('/') ? data : `/${data}`)
          break
        default:
          break
      }
    },
    [leaveSlide, endCurrentStory, finish, tracker, slideIndex, navigate],
  )

  // Открытие стартовой истории (аналитика) — один раз.
  const openedRef = useRef(false)
  useEffect(() => {
    if (openedRef.current) return
    openedRef.current = true
    tracker.openStory(storyMeta(startIndex), 'tray')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Смена слайда: сброс таймингов/паузы, отметка просмотра.
  // Зависим от slide_id (примитив), а не от объекта slide/колбэка advance —
  // иначе обновление кеша ленты после mark пересоздаёт advance и зациклит POST.
  useEffect(() => {
    slideShownAtRef.current = performance.now()
    imageRemainingRef.current = IMAGE_DURATION_MS
    pauseCountRef.current = 0
    videoProgressedRef.current = false
    setPaused(false)
    setVideoDurationMs(null)
    if (!hasAccess || !slide) return
    scheduleMark(slide.slide_id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, slide?.slide_id, scheduleMark])

  // Автопереход картинки с учётом паузы (сохраняет остаток времени).
  useEffect(() => {
    if (!hasAccess || !slide || slide.media_type !== 'image' || paused) return
    const startedAt = performance.now()
    const timer = window.setTimeout(() => advanceRef.current('auto'), imageRemainingRef.current)
    return () => {
      clearTimeout(timer)
      imageRemainingRef.current = Math.max(
        0,
        imageRemainingRef.current - (performance.now() - startedAt),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, slide?.slide_id, slide?.media_type, paused])

  // Пауза/воспроизведение видео (Kinescope-плеер).
  useEffect(() => {
    if (slide?.media_type !== 'video') return
    const p = playerRef.current
    if (!p) return
    if (paused) void p.pause().catch(() => {})
    else void p.play().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, slide?.slide_id, slide?.media_type])

  // Флаш отметки строго при размонтировании.
  useEffect(
    () => () => {
      const pending = pendingRef.current
      if (pending) {
        clearTimeout(pending.timer)
        markMutateRef.current(pending.slideId)
        pendingRef.current = null
      }
    },
    [],
  )

  // Клавиатура: Esc — закрыть, стрелки — листать, пробел — пауза.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') advance('tap_forward')
      else if (e.key === 'ArrowLeft') back()
      else if (e.key === ' ') {
        e.preventDefault()
        setPaused((p) => !p)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close, advance, back])

  if (!story) return null

  // Удержание = пауза, быстрый тап = листание.
  const onZoneDown = (dir: 'next' | 'prev') => (e: React.PointerEvent) => {
    e.preventDefault()
    const timer = window.setTimeout(() => {
      pauseCountRef.current += 1
      setPaused(true)
    }, HOLD_MS)
    holdRef.current = { timer, dir }
  }
  const onZoneUp = () => {
    const h = holdRef.current
    if (!h) return
    clearTimeout(h.timer)
    holdRef.current = null
    // Свайпом вниз историю не листаем — жест обрабатывает карточка.
    if (draggedRef.current) return
    if (pausedRef.current) {
      setPaused(false)
      return
    }
    if (h.dir === 'next') advance('tap_forward')
    else back()
  }
  const onZoneCancel = () => {
    const h = holdRef.current
    if (!h) return
    clearTimeout(h.timer)
    holdRef.current = null
    if (draggedRef.current) return
    if (pausedRef.current) setPaused(false)
  }

  // Свайп вниз закрывает историю (как в нативных сторис). Только тач —
  // на десктопе закрывают крестик, Esc и клик по фону.
  const onCardPointerDown = (e: React.PointerEvent) => {
    draggedRef.current = false
    if (e.pointerType !== 'touch') return
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      t: performance.now(),
      active: false,
    }
  }

  const onCardPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    const dy = e.clientY - d.y
    const dx = e.clientX - d.x
    if (!d.active) {
      if (dy > DRAG_START_PX && dy > Math.abs(dx)) {
        // Это перетаскивание: снимаем удержание/тап и замораживаем показ.
        d.active = true
        draggedRef.current = true
        const h = holdRef.current
        if (h) {
          clearTimeout(h.timer)
          holdRef.current = null
        }
        setPaused(true)
      } else if (Math.abs(dx) > DRAG_START_PX || dy < -DRAG_START_PX) {
        dragRef.current = null
      }
      return
    }
    setDragY(Math.max(0, dy))
  }

  const onCardPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    dragRef.current = null
    if (!d.active) return
    const dy = Math.max(0, e.clientY - d.y)
    const velocity = dy / Math.max(1, performance.now() - d.t)
    if (dy > DRAG_CLOSE_PX || velocity > DRAG_CLOSE_VELOCITY) {
      close()
      return
    }
    // Не дотянул — карточка возвращается на место, показ продолжается.
    setDragY(0)
    setPaused(false)
  }

  /** Жест прерван системой (звонок, шторка) — просто возвращаем карточку. */
  const onCardPointerCancel = () => {
    const d = dragRef.current
    if (!d) return
    dragRef.current = null
    if (!d.active) return
    setDragY(0)
    setPaused(false)
  }

  const hasContent = hasAccess && slides.length > 0
  const activeDurationMs =
    slide?.media_type === 'video'
      ? videoDurationMs ?? IMAGE_DURATION_MS
      : IMAGE_DURATION_MS
  // У платной истории slides пуст — фон берём из иконки, сегменты из slides_count.
  const backdropUrl = slide?.media_url || story.icon_url
  const segmentCount = hasContent
    ? slides.length
    : Math.max(story.slides_count ?? 0, 1)

  return (
    <div className={styles.viewer} role="dialog" aria-modal="true">
      <div className={styles.cluster} ref={clusterRef}>
      {/* Боковая навигация — только десктоп/веб */}
      <div className={styles.side}>
        <button
          type="button"
          className={styles.navStory}
          onClick={() => jumpStory(-1)}
          aria-label="Предыдущая история"
        >
          <SkipIcon dir="left" />
        </button>
        <button
          type="button"
          className={styles.navSlide}
          onClick={back}
          aria-label="Назад"
        >
          <ChevronIcon dir="left" />
        </button>
      </div>

      <div
        className={
          dragY > 0 ? `${styles.card} ${styles.cardDragging}` : styles.card
        }
        style={
          dragY > 0
            ? {
                transform: `translateY(${dragY}px) scale(${Math.max(
                  0.9,
                  1 - dragY / 1000,
                )})`,
                transition: 'none',
              }
            : undefined
        }
        onPointerDown={onCardPointerDown}
        onPointerMove={onCardPointerMove}
        onPointerUp={onCardPointerUp}
        onPointerCancel={onCardPointerCancel}
      >
        {backdropUrl && (
          <div
            className={styles.backdrop}
            style={{ backgroundImage: `url("${backdropUrl}")` }}
          />
        )}

        <div className={styles.stage}>
          {!hasContent ? (
            <div className={styles.locked}>
              <span className={styles.lockIcon}>
                <LockIcon />
              </span>
              <p className={styles.lockedTitle}>Видео недоступно</p>
              <p className={styles.lockedText}>
                Это видео доступно только для подписчиков Secret Chat, чтобы
                разблокировать доступ, оформите подписку
              </p>
            </div>
          ) : slide?.media_type === 'video' ? (
            <div key={slide.slide_id} className={styles.videoWrap}>
              <Player
                ref={playerRef}
                videoId={parseKinescope(slide.media_url).videoId}
                watermark={parseKinescope(slide.media_url).watermark}
                width="100%"
                height="100%"
                autoPlay
                muted
                playsInline
                controls={false}
                mainPlayButton={false}
                /* Без этого Kinescope запоминает позицию просмотра и при
                   повторном открытии стартует с конца ролика: сразу прилетает
                   ended → слайд/история закрываются. Сторис всегда с начала. */
                localStorage={false}
                onTimeUpdate={(data) => {
                  const t = (data as { currentTime?: number })?.currentTime ?? 0
                  if (t > 0.3) videoProgressedRef.current = true
                }}
                onDurationChange={(data) =>
                  setVideoDurationMs(
                    ((data as { duration?: number })?.duration ?? 0) * 1000,
                  )
                }
                onEnded={handleVideoEnded}
                onError={() => {
                  // Плеер инициализировался, но ролик не проигрался — decode.
                  tracker.mediaError({
                    slideId: slide.slide_id,
                    slideIndex: slideIndex + 1,
                    reason: 'decode',
                  })
                  advance('auto')
                }}
              />
            </div>
          ) : (
            <img
              key={slide?.slide_id}
              className={styles.media}
              src={slide?.media_url}
              alt=""
              onError={() => {
                // Картинка не скачалась: сеть или битая ссылка — network.
                if (!slide) return
                tracker.mediaError({
                  slideId: slide.slide_id,
                  slideIndex: slideIndex + 1,
                  reason: 'network',
                })
              }}
            />
          )}

          <div
            className={`${styles.tap} ${styles.tapPrev}`}
            onPointerDown={onZoneDown('prev')}
            onPointerUp={onZoneUp}
            onPointerLeave={onZoneCancel}
            onPointerCancel={onZoneCancel}
          />
          <div
            className={`${styles.tap} ${styles.tapNext}`}
            onPointerDown={onZoneDown('next')}
            onPointerUp={onZoneUp}
            onPointerLeave={onZoneCancel}
            onPointerCancel={onZoneCancel}
          />
        </div>

        {/* Верхний хром: прогресс + шапка */}
        <div className={styles.top}>
          <div className={styles.progress}>
            {Array.from({ length: segmentCount }).map((_, i) => {
              const done = hasContent && i < slideIndex
              const active = hasContent && i === slideIndex
              const fillClass = [
                styles.segFill,
                done && styles.segFillDone,
                active && styles.segFillActive,
              ]
                .filter(Boolean)
                .join(' ')
              return (
                <span key={i} className={styles.seg}>
                  <span
                    className={fillClass}
                    style={
                      active
                        ? {
                            animationDuration: `${activeDurationMs}ms`,
                            animationPlayState: paused ? 'paused' : 'running',
                          }
                        : undefined
                    }
                  />
                </span>
              )
            })}
          </div>

          <div className={styles.header}>
            <div className={styles.who}>
              <img className={styles.avatar} src={story.icon_url} alt="" />
              <span className={styles.name}>{story.title}</span>
            </div>
            <div className={styles.controls}>
              <button
                type="button"
                className={styles.ctrl}
                onClick={() => setPaused((p) => !p)}
                aria-label={paused ? 'Продолжить' : 'Пауза'}
              >
                {paused ? <PlayIcon /> : <PauseIcon />}
              </button>
              <button
                type="button"
                className={styles.ctrl}
                onClick={close}
                aria-label="Закрыть"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        {!hasContent ? (
          <div className={styles.actionBar}>
            <button
              type="button"
              className={styles.action}
              onClick={buySubscription}
            >
              Купить подписку
            </button>
          </div>
        ) : (
          slide &&
          slide.action_type && (
            <div className={styles.actionBar}>
              <button
                type="button"
                className={styles.action}
                onClick={() => handleAction(slide)}
              >
                {slide.action_title || 'Открыть'}
              </button>
            </div>
          )
        )}
      </div>

      <div className={styles.side}>
        <button
          type="button"
          className={styles.navSlide}
          onClick={() => advance('tap_forward')}
          aria-label="Вперёд"
        >
          <ChevronIcon dir="right" />
        </button>
        <button
          type="button"
          className={styles.navStory}
          onClick={() => jumpStory(1)}
          aria-label="Следующая история"
        >
          <SkipIcon dir="right" />
        </button>
      </div>
      </div>
    </div>
  )
}

export default StoryViewer
